import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';

// Global state to track if a connection is already being established
// This prevents multiple WebSocketProvider instances from creating separate connections
let globalConnectionInProgress = false;
let globalWebSocket = null;

// Expose the WebSocket connection to window for emergency cleanup
if (typeof window !== 'undefined') {
  window._globalWebSocket = globalWebSocket;
}

// Connection states
const CONNECTION_STATES = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting'
};

const WebSocketContext = createContext(null);

// Export the hook for using the WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const eventListenersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const pingTimeoutRef = useRef(null);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const lastPongTimeRef = useRef(Date.now());
  
  const MAX_RECONNECT_ATTEMPTS = 50; // Increased from 10 to 50 for longer persistence
  const INITIAL_RECONNECT_DELAY = 1000;
  const PING_INTERVAL = 15000; // Decreased from 30s to 15s for more frequent checks
  const PONG_TIMEOUT = 10000; // Wait 10s for a pong response before considering connection dead

  // Generic listener for all messages
  const addListener = (callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  };

  // Event-specific listeners
  const addEventListener = (eventType, callback) => {
    if (!eventListenersRef.current.has(eventType)) {
      eventListenersRef.current.set(eventType, new Set());
    }
    eventListenersRef.current.get(eventType).add(callback);
    return () => {
      const listeners = eventListenersRef.current.get(eventType);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          eventListenersRef.current.delete(eventType);
        }
      }
    };
  };

  // Function to check if connection is alive using ping-pong
  const checkConnection = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.log('Connection check failed: WebSocket not open, reconnecting...');
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      connectWebSocket();
      return;
    }

    // Check when we last received a pong
    const timeSinceLastPong = Date.now() - lastPongTimeRef.current;
    if (timeSinceLastPong > PING_INTERVAL * 2) {
      console.warn(`No pong received in ${timeSinceLastPong}ms, connection may be dead. Reconnecting...`);
      // Force close and reconnect
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.error('Error closing dead connection:', e);
        }
      }
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      connectWebSocket();
      return;
    }

    // Send ping with timestamp
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ 
          type: 'ping', 
          timestamp: Date.now() 
        }));
        
        // Set timeout for pong response
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = setTimeout(() => {
          console.warn('Pong timeout - no response received, checking connection status');
          checkConnection(); // Recursive check if no pong received
        }, PONG_TIMEOUT);
      } catch (e) {
        console.error('Error sending ping:', e);
        setConnectionState(CONNECTION_STATES.DISCONNECTED);
        connectWebSocket();
      }
    }
  }, []);

  // The main connection function
  const connectWebSocket = useCallback(() => {
    // Use existing connection if available
    if (globalWebSocket && (globalWebSocket.readyState === WebSocket.OPEN || globalWebSocket.readyState === WebSocket.CONNECTING)) {
      console.log('Using existing global WebSocket connection');
      wsRef.current = globalWebSocket;
      
      if (globalWebSocket.readyState === WebSocket.OPEN) {
        setConnectionState(CONNECTION_STATES.CONNECTED);
      } else {
        setConnectionState(CONNECTION_STATES.CONNECTING);
      }
      return;
    }
    
    // If connection is already being established by another instance, wait
    if (globalConnectionInProgress) {
      console.log('Another WebSocketProvider is already connecting - waiting...');
      setConnectionState(CONNECTION_STATES.CONNECTING);
      
      // Set a timeout to check again in a moment
      setTimeout(() => {
        if (globalWebSocket) {
          console.log('Global WebSocket now available, using it');
          wsRef.current = globalWebSocket;
          
          if (globalWebSocket.readyState === WebSocket.OPEN) {
            setConnectionState(CONNECTION_STATES.CONNECTED);
          }
        } else {
          console.log('No global WebSocket available after waiting, trying to connect');
          connectWebSocket();
        }
      }, 500);
      return;
    }

    // Regular connection check
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already exists and is open or connecting - skipping new connection');
      
      if (wsRef.current.readyState === WebSocket.OPEN) {
        setConnectionState(CONNECTION_STATES.CONNECTED);
      } else {
        setConnectionState(CONNECTION_STATES.CONNECTING);
      }
      return;
    }

    // Mark connection as in progress to prevent duplicates
    globalConnectionInProgress = true;
    setConnectionState(CONNECTION_STATES.CONNECTING);
    
    const currentAttempt = connectionAttempts + 1;
    console.log(`Attempting WebSocket connection to ${process.env.REACT_APP_WEBSOCKET_URL} (attempt ${currentAttempt})...`);
    console.trace('WebSocket connection creation stack trace:');
    
    try {
      // Close existing connection if somehow it's still there
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.error('Error closing existing connection:', e);
        }
      }
      
      // Create new connection
      wsRef.current = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL);
      globalWebSocket = wsRef.current;
      if (typeof window !== 'undefined') {
        window._globalWebSocket = globalWebSocket;
      }
    
      wsRef.current.onopen = () => {
        console.log(`WebSocket connection established to ${process.env.REACT_APP_WEBSOCKET_URL}`);
        console.log('WebSocket readyState:', wsRef.current.readyState);
        globalConnectionInProgress = false;
        setConnectionState(CONNECTION_STATES.CONNECTED);
        setConnectionAttempts(0); // Reset connection attempts on successful connection
        lastPongTimeRef.current = Date.now(); // Reset pong timer on successful connection
        
        if (currentAttempt > 1) {
          // toast.success('WebSocket connection re-established');
        }
      };
  
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle ping-pong for connection monitoring
          if (data.type === 'pong' || data.type === 'ping') {
            // Update last pong time
            lastPongTimeRef.current = Date.now();
            // Clear the pong waiting timeout
            clearTimeout(pingTimeoutRef.current);
            pingTimeoutRef.current = null;
            
            if (data.type === 'pong') {
              console.log('Received pong from server');
            } else if (data.type === 'ping') {
              console.log('Received ping from server, sending pong');
              // Reply with pong if server sent a ping
              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  type: 'pong',
                  timestamp: Date.now(),
                  replyTo: data.timestamp
                }));
              }
            }
            return;
          }
          
          console.log('WebSocket message received:', data);
          
          // Notify all generic listeners
          listenersRef.current.forEach(listener => listener(data));
          
          // Notify event-specific listeners
          if (data.script && eventListenersRef.current.has(data.script)) {
            eventListenersRef.current.get(data.script).forEach(listener => listener(data));
          }
          
          // Also notify by status (success/error)
          if (data.status && eventListenersRef.current.has(data.status)) {
            eventListenersRef.current.get(data.status).forEach(listener => listener(data));
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };
  
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        globalConnectionInProgress = false;
        setConnectionState(CONNECTION_STATES.DISCONNECTED);
      };
  
      wsRef.current.onclose = (event) => {
        console.log(`WebSocket connection closed (code: ${event.code}, reason: ${event.reason || 'No reason provided'})`);
        globalConnectionInProgress = false;
        globalWebSocket = null;
        if (typeof window !== 'undefined') {
          window._globalWebSocket = null;
        }
        
        setConnectionState(CONNECTION_STATES.DISCONNECTED);
        
        // Don't attempt to reconnect if closed cleanly during unmount
        if (event.wasClean && (event.code === 1000 || event.code === 1001)) {
          console.log('Clean WebSocket closure, not attempting to reconnect');
          return;
        }
        
        // Attempt reconnection with exponential backoff
        if (currentAttempt < MAX_RECONNECT_ATTEMPTS) {
          setConnectionState(CONNECTION_STATES.RECONNECTING);
          
          // Cap the max delay at 30 seconds to prevent extremely long waits
          const delay = Math.min(30000, INITIAL_RECONNECT_DELAY * Math.pow(1.5, Math.min(currentAttempt, 15)));
          console.log(`Attempting to reconnect in ${delay}ms... (attempt ${currentAttempt}/${MAX_RECONNECT_ATTEMPTS})`);
          
          // Show toast for first few reconnection attempts
          if (currentAttempt < 3) {
            console.log(`Connection lost. Reconnecting in ${Math.round(delay/1000)} seconds...`);
          } else if (currentAttempt % 5 === 0) { // Show occasional updates for longer reconnection sequences
            console.log(`Still trying to reconnect (attempt ${currentAttempt}/${MAX_RECONNECT_ATTEMPTS})`);
          }
          
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            setConnectionAttempts(currentAttempt);
            connectWebSocket();
          }, delay);
        } else {
          console.error('Maximum WebSocket reconnection attempts reached');
          toast.error('Unable to connect to server. Please refresh the page to try again.');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      globalConnectionInProgress = false;
      globalWebSocket = null;
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      
      // Still attempt to reconnect after error
      const delay = Math.min(30000, INITIAL_RECONNECT_DELAY * Math.pow(1.5, Math.min(currentAttempt, 15)));
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        setConnectionAttempts(currentAttempt);
        connectWebSocket();
      }, delay);
    }
  }, [connectionAttempts]);

  // Effect to establish initial connection
  useEffect(() => {
    connectWebSocket();

    // Online/offline event listeners to detect network changes
    const handleOnline = () => {
      console.log('Network is online, reconnecting WebSocket...');
      connectWebSocket();
    };

    const handleOffline = () => {
      console.log('Network is offline, WebSocket will reconnect when online');
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
    };

    // Visibility change listener to reconnect when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab is now visible, checking WebSocket connection...');
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          connectWebSocket();
        } else {
          // Even if the connection appears open, verify it's working
          checkConnection();
        }
      }
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Setup ping interval to keep connection alive and detect broken connections
    const pingInterval = setInterval(() => {
      checkConnection();
    }, PING_INTERVAL);

    // Cleanup function
    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeoutRef.current);
      clearTimeout(pingTimeoutRef.current);
      
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Don't close the connection if it's being used by other components
      // Just clean up our references instead
      wsRef.current = null;
    };
  }, [connectWebSocket, checkConnection]);

  // Effect to show UI notifications about connection state changes
  useEffect(() => {
    // Only show notifications for state transitions that users should be aware of
    switch (connectionState) {
      case CONNECTION_STATES.CONNECTED:
        // We already show a toast when reconnecting in the onopen handler
        break;
      case CONNECTION_STATES.DISCONNECTED:
        // Handled by reconnection logic
        break;
      case CONNECTION_STATES.RECONNECTING:
        // Handled by reconnection logic
        break;
      default:
        break;
    }
  }, [connectionState]);

  // Method to send data through the WebSocket
  const sendMessage = (data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    // Auto-reconnect if not connected
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.CONNECTING) {
      console.log('Cannot send message - WebSocket not connected. Reconnecting...');
      connectWebSocket();
    }
    return false;
  };

  // Force a reconnection (can be called from UI if needed)
  const forceReconnect = useCallback(() => {
    console.log('Forcing WebSocket reconnection...');
    
    // Close existing connection
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        console.error('Error closing connection during forced reconnect:', e);
      }
    }
    
    // Reset state and reconnect
    setConnectionState(CONNECTION_STATES.RECONNECTING);
    setConnectionAttempts(0);
    connectWebSocket();
  }, [connectWebSocket]);

  return (
    <WebSocketContext.Provider value={{ 
      addListener, 
      addEventListener,
      sendMessage,
      forceReconnect,
      connectionState,
      isConnected: connectionState === CONNECTION_STATES.CONNECTED
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}; 